/**
 * Ratings Context
 * Manages ratings for clinics and doctors
 * Phase 1: Internal ratings from verified TreatX patients
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const RatingsContext = createContext();

export const useRatings = () => {
  const context = useContext(RatingsContext);
  if (!context) {
    throw new Error('useRatings must be used within RatingsProvider');
  }
  return context;
};

// Initial mock ratings data
const initialClinicRatings = [
  {
    id: 'cr-001',
    clinicId: 'istanbul-aesthetics',
    patientId: 'patient-001',
    patientName: 'John D.',
    rating: 5,
    review: 'Excellent experience from start to finish. The team was professional and caring.',
    treatmentType: 'Hair Transplant',
    verified: true,
    createdAt: '2024-11-15',
    helpful: 12
  },
  {
    id: 'cr-002',
    clinicId: 'istanbul-aesthetics',
    patientId: 'patient-002',
    patientName: 'Sarah M.',
    rating: 4,
    review: 'Great results and friendly staff. Minor communication delays but overall very happy.',
    treatmentType: 'Rhinoplasty',
    verified: true,
    createdAt: '2024-10-20',
    helpful: 8
  },
  {
    id: 'cr-003',
    clinicId: 'ankara-hair',
    patientId: 'patient-003',
    patientName: 'Michael R.',
    rating: 5,
    review: 'Top-notch facility and exceptional care. Highly recommend!',
    treatmentType: 'Hair Transplant',
    verified: true,
    createdAt: '2024-09-10',
    helpful: 15
  }
];

const initialDoctorRatings = [
  {
    id: 'dr-001',
    doctorId: 'doctor-001',
    doctorName: 'Dr. Mehmet Yilmaz',
    patientId: 'patient-001',
    patientName: 'John D.',
    rating: 5,
    review: 'Dr. Yilmaz is incredibly skilled. He explained everything clearly and the results exceeded my expectations.',
    treatmentType: 'Hair Transplant',
    verified: true,
    createdAt: '2024-11-15',
    helpful: 10
  },
  {
    id: 'dr-002',
    doctorId: 'doctor-001',
    doctorName: 'Dr. Mehmet Yilmaz',
    patientId: 'patient-002',
    patientName: 'Emma L.',
    rating: 5,
    review: 'Absolutely fantastic surgeon. Very gentle and professional.',
    treatmentType: 'Hair Transplant',
    verified: true,
    createdAt: '2024-10-05',
    helpful: 7
  },
  {
    id: 'dr-003',
    doctorId: 'doctor-002',
    doctorName: 'Dr. Aylin Kara',
    patientId: 'patient-003',
    patientName: 'Lisa P.',
    rating: 4,
    review: 'Great cosmetic surgeon with artistic vision. Very happy with my results.',
    treatmentType: 'Rhinoplasty',
    verified: true,
    createdAt: '2024-09-28',
    helpful: 5
  }
];

export const RatingsProvider = ({ children }) => {
  const [clinicRatings, setClinicRatings] = useState(initialClinicRatings);
  const [doctorRatings, setDoctorRatings] = useState(initialDoctorRatings);

  // Get average rating for a clinic
  const getClinicAverageRating = useCallback((clinicId) => {
    const ratings = clinicRatings.filter(r => r.clinicId === clinicId);
    if (ratings.length === 0) return { average: 0, count: 0 };
    const sum = ratings.reduce((acc, r) => acc + r.rating, 0);
    return {
      average: Math.round((sum / ratings.length) * 10) / 10,
      count: ratings.length
    };
  }, [clinicRatings]);

  // Get average rating for a doctor
  const getDoctorAverageRating = useCallback((doctorId) => {
    const ratings = doctorRatings.filter(r => r.doctorId === doctorId);
    if (ratings.length === 0) return { average: 0, count: 0 };
    const sum = ratings.reduce((acc, r) => acc + r.rating, 0);
    return {
      average: Math.round((sum / ratings.length) * 10) / 10,
      count: ratings.length
    };
  }, [doctorRatings]);

  // Get all ratings for a clinic
  const getClinicRatings = useCallback((clinicId) => 
    clinicRatings.filter(r => r.clinicId === clinicId), [clinicRatings]);

  // Get all ratings for a doctor
  const getDoctorRatings = useCallback((doctorId) => 
    doctorRatings.filter(r => r.doctorId === doctorId), [doctorRatings]);

  // Add clinic rating
  const addClinicRating = useCallback((ratingData) => {
    const newRating = {
      id: `cr-${Date.now()}`,
      verified: true,
      createdAt: new Date().toISOString().split('T')[0],
      helpful: 0,
      ...ratingData
    };
    setClinicRatings(prev => [...prev, newRating]);
    return newRating;
  }, []);

  // Add doctor rating
  const addDoctorRating = useCallback((ratingData) => {
    const newRating = {
      id: `dr-${Date.now()}`,
      verified: true,
      createdAt: new Date().toISOString().split('T')[0],
      helpful: 0,
      ...ratingData
    };
    setDoctorRatings(prev => [...prev, newRating]);
    return newRating;
  }, []);

  // Mark rating as helpful
  const markClinicRatingHelpful = useCallback((ratingId) => {
    setClinicRatings(prev => prev.map(r => 
      r.id === ratingId ? { ...r, helpful: r.helpful + 1 } : r
    ));
  }, []);

  const markDoctorRatingHelpful = useCallback((ratingId) => {
    setDoctorRatings(prev => prev.map(r => 
      r.id === ratingId ? { ...r, helpful: r.helpful + 1 } : r
    ));
  }, []);

  // Generate star display component data
  const getStarDisplay = useCallback((rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    return { fullStars, hasHalfStar, emptyStars };
  }, []);

  const value = {
    clinicRatings,
    doctorRatings,
    getClinicAverageRating,
    getDoctorAverageRating,
    getClinicRatings,
    getDoctorRatings,
    addClinicRating,
    addDoctorRating,
    markClinicRatingHelpful,
    markDoctorRatingHelpful,
    getStarDisplay
  };

  return (
    <RatingsContext.Provider value={value}>
      {children}
    </RatingsContext.Provider>
  );
};

export default RatingsContext;
